import { ICountryConfig } from '@query'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { TFunction } from 'i18next'
import { uniqBy } from 'lodash'
import { ValidLocale } from '@localisation'

dayjs.extend(isSameOrBefore)

export * from './urls'

export enum NocfoPlan {
  FREE = 'FREE',
  PLUS = 'PLUS',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE'
}

export const NOCFO_PLAN_OPTIONS = [
  {
    labelKey: `constants.nocfoPlan.${NocfoPlan.FREE}`,
    value: NocfoPlan.FREE
  },
  {
    labelKey: `constants.nocfoPlan.${NocfoPlan.PLUS}`,
    value: NocfoPlan.PLUS
  },
  {
    labelKey: `constants.nocfoPlan.${NocfoPlan.PRO}`,
    value: NocfoPlan.PRO
  },
  {
    labelKey: `constants.nocfoPlan.${NocfoPlan.ENTERPRISE}`,
    value: NocfoPlan.ENTERPRISE
  }
]

export enum NocfoPlanSource {
  STRIPE = 'STRIPE',
  AGENCY = 'AGENCY',
  MANUAL = 'MANUAL'
}

export enum VatCode {
  TAX_FREE = 3,
  DOMESTIC_SALES_TAX = 1,
  DOMESTIC_PURCHASE_TAX = 2,
  DOMESTIC_ZERO_TAX_RATE = 4,
  EU_SALE_OF_GOODS = 5,
  EU_SALE_OF_SERVICES = 6,
  EU_PURCHASE_OF_GOODS = 7,
  EU_PURCHASE_OF_SERVICES = 8,
  PURCHASE_OF_GOODS_OUTSIDE_EU = 9,
  PURCHASE_OF_SERVICES_OUTSIDE_EU = 14,
  SALE_OF_CONSTRUCTION_SERVICES = 10,
  PURCHASE_OF_CONSTRUCTION_SERVICES = 11,
  SALE_OF_GOODS_OUTSIDE_EU = 12,
  SALE_OF_SERVICES_OUTSIDE_EU = 13
}

export enum DocumentType {
  MANUAL_ENTRY = 0,
  VAT_ENTRY = 3,
  DEPRECATION = 4,
  INCOME_TAX = 6
}

export enum BlueprintType {
  PURCHASE = 'PURCHASE',
  SALES = 'SALES',
  MANUAL = 'MANUAL'
}

export const BLUEPRINT_TYPE_OPTIONS = [
  {
    labelKey: 'constants.documentType.purchase',
    value: BlueprintType.PURCHASE
  },
  {
    labelKey: 'constants.documentType.sales',
    value: BlueprintType.SALES
  },
  {
    labelKey: 'constants.documentType.other',
    value: BlueprintType.MANUAL
  }
]

export type AIBlueprintType = BlueprintType.PURCHASE | BlueprintType.SALES

export const AI_BLUEPRINT_TYPE_OPTIONS: Array<{
  labelKey: string
  value: AIBlueprintType
}> = [
  {
    labelKey: 'constants.documentType.purchase',
    value: BlueprintType.PURCHASE
  },
  {
    labelKey: 'constants.documentType.sales',
    value: BlueprintType.SALES
  }
]

export enum AccountType {
  ASS = 'ASS', // Vastaavaa
  ASS_DEP = 'ASS_DEP', // Poistokelpoinen omaisuus
  ASS_VAT = 'ASS_VAT', // Arvonlisäverosaatava
  ASS_REC = 'ASS_REC', // Siirtosaamiset
  ASS_PAY = 'ASS_PAY', // Pankkitili / käteisvarat
  ASS_DUE = 'ASS_DUE', // Myyntisaatavat

  LIA = 'LIA', // Vastattavaa
  LIA_EQU = 'LIA_EQU', // Oma pääoma
  LIA_PRE = 'LIA_PRE', // Edellisten tilikausien voitto
  LIA_DUE = 'LIA_DUE', // Ostovelat
  LIA_DEB = 'LIA_DEB', // Velat
  LIA_ACC = 'LIA_ACC', // Siirtovelat
  LIA_VAT = 'LIA_VAT', // Arvonlisäverovelka

  REV = 'REV', // Tulot
  REV_SAL = 'REV_SAL', // Liikevaihtotulot (myynti)
  REV_NO = 'REV_NO', // Verottomat tulot

  EXP = 'EXP', // Menot
  EXP_DEP = 'EXP_DEP', // Poistot
  EXP_NO = 'EXP_NO', // Vähennyskelvottomat menot
  EXP_50 = 'EXP_50', // Puoliksi vähennyskelpoiset menot
  EXP_TAX = 'EXP_TAX', // Verotili
  EXP_TAX_PRE = 'EXP_TAX_PRE' // Ennakkoverot
}

export const ACCOUNT_TYPES = [
  { value: AccountType.ASS, labelKey: 'constants.accountType.ASS' },
  { value: AccountType.ASS_DEP, labelKey: 'constants.accountType.ASS_DEP' },
  { value: AccountType.ASS_VAT, labelKey: 'constants.accountType.ASS_VAT' },
  { value: AccountType.ASS_REC, labelKey: 'constants.accountType.ASS_REC' },
  { value: AccountType.ASS_PAY, labelKey: 'constants.accountType.ASS_PAY' },
  { value: AccountType.ASS_DUE, labelKey: 'constants.accountType.ASS_DUE' },
  { value: AccountType.LIA, labelKey: 'constants.accountType.LIA' },
  { value: AccountType.LIA_EQU, labelKey: 'constants.accountType.LIA_EQU' },
  { value: AccountType.LIA_PRE, labelKey: 'constants.accountType.LIA_PRE' },
  { value: AccountType.LIA_DUE, labelKey: 'constants.accountType.LIA_DUE' },
  { value: AccountType.LIA_DEB, labelKey: 'constants.accountType.LIA_DEB' },
  { value: AccountType.LIA_ACC, labelKey: 'constants.accountType.LIA_ACC' },
  { value: AccountType.LIA_VAT, labelKey: 'constants.accountType.LIA_VAT' },
  { value: AccountType.REV, labelKey: 'constants.accountType.REV' },
  { value: AccountType.REV_SAL, labelKey: 'constants.accountType.REV_SAL' },
  { value: AccountType.REV_NO, labelKey: 'constants.accountType.REV_NO' },
  { value: AccountType.EXP, labelKey: 'constants.accountType.EXP' },
  { value: AccountType.EXP_DEP, labelKey: 'constants.accountType.EXP_DEP' },
  { value: AccountType.EXP_NO, labelKey: 'constants.accountType.EXP_NO' },
  { value: AccountType.EXP_50, labelKey: 'constants.accountType.EXP_50' },
  { value: AccountType.EXP_TAX, labelKey: 'constants.accountType.EXP_TAX' },
  { value: AccountType.EXP_TAX_PRE, labelKey: 'constants.accountType.EXP_TAX_PRE' }
]

export const NOTIFYABLE_ERROR_CODES = [423, 409, 412, 428, 403]

export enum ReportType {
  LEDGER = 'LEDGER',
  JOURNAL = 'JOURNAL',
  BALANCE_SHEET = 'BALANCE_SHEET',
  BALANCE_SHEET_SHORT = 'BALANCE_SHEET_SHORT',
  BALANCE_SHEET_RECONCILIATION = 'BALANCE_SHEET_RECONCILIATION',
  INCOME_STATEMENT = 'INCOME_STATEMENT',
  INCOME_STATEMENT_SHORT = 'INCOME_STATEMENT_SHORT',
  VAT_REPORT = 'VAT_REPORT',
  EU_VAT_CONTACT_REPORT = 'EU_VAT_CONTACT_REPORT'
}

export const REPORT_TYPE_OPTIONS = [
  {
    labelKey: 'constants.reportType.ledger',
    value: ReportType.LEDGER
  },
  {
    labelKey: 'constants.reportType.journal',
    value: ReportType.JOURNAL
  },
  {
    labelKey: 'constants.reportType.balanceSheet',
    value: ReportType.BALANCE_SHEET
  },
  {
    labelKey: 'constants.reportType.incomeStatement',
    value: ReportType.INCOME_STATEMENT
  },
  {
    labelKey: 'constants.reportType.vatReport',
    value: ReportType.VAT_REPORT
  },
  {
    labelKey: 'constants.reportType.euVatContactReport',
    value: ReportType.EU_VAT_CONTACT_REPORT
  }
]

export enum ReportRenderFormat {
  PDF = 'pdf',
  MARKDOWN = 'md',
  CSV = 'csv',
  DOCX = 'docx'
}

export const REPORT_RENDER_FORMATS_BY_TYPE: Record<ReportType, ReportRenderFormat[]> = {
  [ReportType.LEDGER]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.JOURNAL]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.BALANCE_SHEET]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.BALANCE_SHEET_SHORT]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.BALANCE_SHEET_RECONCILIATION]: [ReportRenderFormat.DOCX, ReportRenderFormat.CSV],
  [ReportType.INCOME_STATEMENT]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.INCOME_STATEMENT_SHORT]: [ReportRenderFormat.PDF, ReportRenderFormat.CSV],
  [ReportType.VAT_REPORT]: [ReportRenderFormat.PDF],
  [ReportType.EU_VAT_CONTACT_REPORT]: [ReportRenderFormat.PDF]
}

export enum VatPeriod {
  NONE = -1,
  MONTH = 0,
  QUARTER = 1,
  YEAR = 2
}

export const VAT_PERIOD_OPTIONS = [
  {
    labelKey: 'constants.vatPeriod.none',
    value: VatPeriod.NONE
  },
  {
    labelKey: 'constants.vatPeriod.month',
    value: VatPeriod.MONTH
  },
  {
    labelKey: 'constants.vatPeriod.quarter',
    value: VatPeriod.QUARTER
  },
  {
    labelKey: 'constants.vatPeriod.year',
    value: VatPeriod.YEAR
  }
]

export enum VatPostingMethod {
  NET = 'NET',
  SPLIT = 'SPLIT'
}

export enum ContactType {
  PERSON = 'PERSON',
  BUSINESS = 'BUSINESS'
}

export const CONTACT_TYPE_OPTIONS = [
  {
    labelKey: 'constants.contact.person',
    value: ContactType.PERSON
  },
  {
    labelKey: 'constants.contact.business',
    value: ContactType.BUSINESS
  }
]
export enum AnalyticsFrequency {
  D = 'D',
  W = 'W',
  M = 'M',
  Q = 'Q',
  Y = 'Y'
}

export enum AnalyticsRange {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  MONTH_PREV = 'MONTH_PREV',
  PERIOD_CURRENT = 'PERIOD_CURRENT',
  PERIOD_PREVIOUS = 'PERIOD_PREVIOUS',
  PERIOD_LATEST = 'PERIOD_LATEST',
  CUSTOM = 'CUSTOM'
}

export const isCustomRange = (r: AnalyticsRange): boolean => r === AnalyticsRange.CUSTOM

export enum COASize {
  LG = 1,
  MD = 2,
  SM = 3
}

export const COA_SIZE_OPTIONS = [
  {
    labelKey: 'constants.coaSize.lg.name',
    descriptionKey: 'constants.coaSize.lg.description',
    value: COASize.LG
  },
  {
    labelKey: 'constants.coaSize.md.name',
    descriptionKey: 'constants.coaSize.md.description',
    value: COASize.MD
  },
  {
    labelKey: 'constants.coaSize.sm.name',
    descriptionKey: 'constants.coaSize.sm.description',
    value: COASize.SM
  }
]

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  ACCEPTED = 'ACCEPTED',
  PAID = 'PAID',
  CREDIT_LOSS = 'CREDIT_LOSS'
}

export enum InvoiceAutomaticDebtCollectionStatus {
  NOT_STARTED = 'NOT_STARTED',
  EMAIL_SENT = 'EMAIL_SENT',
  SENT_TO_KRAVIA = 'SENT_TO_KRAVIA',
  FAILED = 'FAILED'
}

export enum InvoiceSoftReminderStatus {
  NOT_STARTED = 'NOT_STARTED',
  EMAIL_SENT = 'EMAIL_SENT',
  FAILED = 'FAILED'
}

export enum NotificationType {
  CUSTOM = 'CUSTOM',
  FILE_UPLOADED_VIA_EMAIL = 'FILE_UPLOADED_VIA_EMAIL',
  INVOICE_RECEIVED = 'INVOICE_RECEIVED',
  VAT_PERIOD_REPORTING_DUE_1 = 'VAT_PERIOD_REPORTING_DUE_1',
  VAT_PERIOD_REPORTING_DUE_TOMORROW = 'VAT_PERIOD_REPORTING_DUE_TOMORROW',
  VAT_PERIOD_REPORTING_DUE_2 = 'VAT_PERIOD_REPORTING_DUE_2',
  BANK_INTEGRATION_SUSPENDED = 'BANK_INTEGRATION_SUSPENDED',
  BANK_INTEGRATION_GOING_TO_EXPIRE = 'BANK_INTEGRATION_GOING_TO_EXPIRE',
  HOLVI_INTEGRATION_SUSPENDED = 'HOLVI_INTEGRATION_SUSPENDED',
  USER_MENTIONED_IN_COMMENT = 'USER_MENTIONED_IN_COMMENT',
  ACCOUNTING_SUGGESTIONS_AVAILABLE = 'ACCOUNTING_SUGGESTIONS_AVAILABLE',
  PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS = 'PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS',
  PURCHASE_INVOICE_DUE = 'PURCHASE_INVOICE_DUE',
  SALES_INVOICE_OVERDUE = 'SALES_INVOICE_OVERDUE',
  AI_CHAT_MESSAGE = 'AI_CHAT_MESSAGE',
  CUSTOMER_SUPPORT_MESSAGE = 'CUSTOMER_SUPPORT_MESSAGE',
  APP_RATING_REQUEST = 'APP_RATING_REQUEST',
  FEATURE_ANNOUNCEMENT = 'FEATURE_ANNOUNCEMENT',
  SERVICE_STATUS_ALERT = 'SERVICE_STATUS_ALERT',
  EXPENSE_CLAIM_AWAITING_APPROVAL = 'EXPENSE_CLAIM_AWAITING_APPROVAL',
  EXPENSE_CLAIM_APPROVED = 'EXPENSE_CLAIM_APPROVED',
  EXPENSE_CLAIM_REJECTED = 'EXPENSE_CLAIM_REJECTED',
  EXPENSE_CLAIM_APPROVAL_CANCELLED = 'EXPENSE_CLAIM_APPROVAL_CANCELLED',
  EXPENSE_CLAIM_APPROVAL_REMINDER = 'EXPENSE_CLAIM_APPROVAL_REMINDER'
}

/**
 * Types omitted from web bell unseen_counts and inbox list.
 */
export const NOTIFICATION_UNSEEN_COUNT_EXCLUDE_TYPES: readonly NotificationType[] = [
  NotificationType.SALES_INVOICE_OVERDUE,
  NotificationType.APP_RATING_REQUEST,
  NotificationType.CUSTOMER_SUPPORT_MESSAGE,
  NotificationType.FEATURE_ANNOUNCEMENT,
  NotificationType.SERVICE_STATUS_ALERT
]

export const notificationTypesOmittedOnWebSet: ReadonlySet<NotificationType> = new Set(
  NOTIFICATION_UNSEEN_COUNT_EXCLUDE_TYPES
)

export enum NotificationIntent {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  DANGER = 'DANGER'
}

export const SUPPORTED_LANGUAGES = [
  {
    value: ValidLocale.FI,
    labelKey: 'language.fi'
  },
  {
    value: ValidLocale.SV,
    labelKey: 'language.sv'
  },
  {
    value: ValidLocale.EN,
    labelKey: 'language.en'
  },
  {
    value: ValidLocale.DE,
    labelKey: 'language.de'
  }
]

export enum InvoiceDeliveryMethod {
  EINVOICE = 'EINVOICE',
  EMAIL = 'EMAIL',
  EMAIL_XRECHNUNG = 'EMAIL_XRECHNUNG',
  ELASKU = 'ELASKU',
  PAPER = 'PAPER',
  SELF_PRINT = 'SELF_PRINT'
}

export enum VatRateLabel {
  STANDARD = 'standard',
  REDUCED_A = 'reduced_a',
  REDUCED_B = 'reduced_b',
  ZERO = 'zero'
}

export interface VatRateOption {
  label: string
  value: string
}

interface GetVatRateLabelOptionsProps {
  countryConfig: ICountryConfig | null | undefined
  dateAt: dayjs.Dayjs
  t: TFunction
  uniqueLabels?: boolean
}

export const getVatRateLabelOptions = ({
  countryConfig,
  dateAt,
  t,
  uniqueLabels = false
}: GetVatRateLabelOptionsProps): VatRateOption[] => {
  if (!countryConfig?.vat_configs?.length) {
    return []
  }

  const vatConfig = [...countryConfig.vat_configs]
    .sort(({ effective_from: a }, { effective_from: b }) =>
      dayjs(b, 'YYYY-MM-DD').diff(dayjs(a, 'YYYY-MM-DD'), 'days')
    )
    .find(({ effective_from }) => dayjs(effective_from, 'YYYY-MM-DD').isSameOrBefore(dateAt))

  const options =
    vatConfig?.vat_rate_options.map(({ label, rate }) => ({
      label: t(`constants.vatRate.${label}`, { rate }),
      value: label
    })) || []

  return uniqueLabels ? uniqBy(options, 'value') : options
}

export const TIME_CONSTANTS = {
  SECONDS_IN_MINUTE: 60,
  MS_IN_SECOND: 1000
}

export enum InvoiceRepetitionOptions {
  NO_SELECTION = -1,
  MONTHLY = 1,
  YEARLY = 0
}

export enum InvoiceRecurrenceMode {
  NONE = 'none',
  REPEATS = 'repeats'
}

export const INVOICE_RECURRENCE_MODE_OPTIONS = [
  {
    labelKey: `constants.invoiceRepetition.modes.${InvoiceRecurrenceMode.NONE}`,
    value: InvoiceRecurrenceMode.NONE
  },
  {
    labelKey: `constants.invoiceRepetition.modes.${InvoiceRecurrenceMode.REPEATS}`,
    value: InvoiceRecurrenceMode.REPEATS
  }
]

export enum InvoiceRecurrencePreset {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  SEMI_ANNUAL = 'semi_annual',
  CUSTOM = 'custom',
  YEARLY = 'yearly'
}

export const INVOICE_RECURRENCE_PRESET_OPTIONS = [
  {
    labelKey: `constants.invoiceRepetition.presets.${InvoiceRecurrencePreset.MONTHLY}`,
    value: InvoiceRecurrencePreset.MONTHLY
  },
  {
    labelKey: `constants.invoiceRepetition.presets.${InvoiceRecurrencePreset.QUARTERLY}`,
    value: InvoiceRecurrencePreset.QUARTERLY
  },
  {
    labelKey: `constants.invoiceRepetition.presets.${InvoiceRecurrencePreset.SEMI_ANNUAL}`,
    value: InvoiceRecurrencePreset.SEMI_ANNUAL
  },
  {
    labelKey: `constants.invoiceRepetition.presets.${InvoiceRecurrencePreset.YEARLY}`,
    value: InvoiceRecurrencePreset.YEARLY
  },
  {
    labelKey: `constants.invoiceRepetition.presets.${InvoiceRecurrencePreset.CUSTOM}`,
    value: InvoiceRecurrencePreset.CUSTOM
  }
]

const RECURRENCE_PRESET_INTERVALS: Record<
  Exclude<InvoiceRecurrencePreset, InvoiceRecurrencePreset.CUSTOM | InvoiceRecurrencePreset.YEARLY>,
  number
> = {
  [InvoiceRecurrencePreset.MONTHLY]: 1,
  [InvoiceRecurrencePreset.QUARTERLY]: 3,
  [InvoiceRecurrencePreset.SEMI_ANNUAL]: 6
}

export const recurrencePresetToInterval = (preset: InvoiceRecurrencePreset): number | null => {
  if (preset === InvoiceRecurrencePreset.CUSTOM || preset === InvoiceRecurrencePreset.YEARLY) {
    return null
  }

  return RECURRENCE_PRESET_INTERVALS[preset]
}

export const intervalToRecurrencePreset = (interval?: number): InvoiceRecurrencePreset => {
  if (interval === RECURRENCE_PRESET_INTERVALS[InvoiceRecurrencePreset.QUARTERLY]) {
    return InvoiceRecurrencePreset.QUARTERLY
  }

  if (interval === RECURRENCE_PRESET_INTERVALS[InvoiceRecurrencePreset.SEMI_ANNUAL]) {
    return InvoiceRecurrencePreset.SEMI_ANNUAL
  }

  if (!interval || interval === RECURRENCE_PRESET_INTERVALS[InvoiceRecurrencePreset.MONTHLY]) {
    return InvoiceRecurrencePreset.MONTHLY
  }

  return InvoiceRecurrencePreset.CUSTOM
}

export const recurrenceModeFromFrequency = (frequency?: number): InvoiceRecurrenceMode => {
  if (frequency === undefined || frequency === InvoiceRepetitionOptions.NO_SELECTION) {
    return InvoiceRecurrenceMode.NONE
  }

  return InvoiceRecurrenceMode.REPEATS
}

export const recurrenceScheduleFromRule = (rule?: {
  frequency?: number
  interval?: number
}): InvoiceRecurrencePreset => {
  if (rule?.frequency === InvoiceRepetitionOptions.YEARLY) {
    return InvoiceRecurrencePreset.YEARLY
  }

  if (rule?.frequency === InvoiceRepetitionOptions.MONTHLY) {
    return intervalToRecurrencePreset(rule.interval)
  }

  return InvoiceRecurrencePreset.MONTHLY
}

export const getRuleFieldsForRecurrencePreset = (
  preset: InvoiceRecurrencePreset,
  customInterval?: number
): { frequency: InvoiceRepetitionOptions; interval: number } => {
  if (preset === InvoiceRecurrencePreset.YEARLY) {
    return { frequency: InvoiceRepetitionOptions.YEARLY, interval: 1 }
  }

  const interval = recurrencePresetToInterval(preset) ?? customInterval ?? 1

  return { frequency: InvoiceRepetitionOptions.MONTHLY, interval }
}

export enum InvoiceRepetitionDays {
  DAY_1 = 1,
  DAY_2 = 2,
  DAY_3 = 3,
  DAY_4 = 4,
  DAY_5 = 5,
  DAY_6 = 6,
  DAY_7 = 7,
  DAY_8 = 8,
  DAY_9 = 9,
  DAY_10 = 10,
  DAY_11 = 11,
  DAY_12 = 12,
  DAY_13 = 13,
  DAY_14 = 14,
  DAY_15 = 15,
  DAY_16 = 16,
  DAY_17 = 17,
  DAY_18 = 18,
  DAY_19 = 19,
  DAY_20 = 20,
  DAY_21 = 21,
  DAY_22 = 22,
  DAY_23 = 23,
  DAY_24 = 24,
  DAY_25 = 25,
  DAY_26 = 26,
  DAY_27 = 27,
  DAY_28 = 28,
  DAY_LAST = -1
}

export const INVOICE_REPETITION_DAYS = Object.keys(InvoiceRepetitionDays)
  .filter(key => !isNaN(InvoiceRepetitionDays[key]))
  .map(key => ({
    value: InvoiceRepetitionDays[key],
    labelKey:
      InvoiceRepetitionDays[key] === InvoiceRepetitionDays.DAY_LAST
        ? `constants.invoiceRepetition.days.${key}`
        : null
  }))

export enum InvoiceRepetitionMonths {
  JAN,
  FEB,
  MAR,
  APR,
  MAY,
  JUN,
  JUL,
  AUG,
  SEP,
  OCT,
  NOV,
  DEC
}

export const INVOICE_REPETITION_MONTHS = Object.keys(InvoiceRepetitionMonths)
  .filter(key => !isNaN(InvoiceRepetitionMonths[key]))
  .map(key => {
    return {
      value: InvoiceRepetitionMonths[key] + 1,
      labelKey: `constants.invoiceRepetition.months.${key}`
    }
  })

export enum ReferenceMethods {
  keep = 'KEEP',
  roll = 'ROLL'
}

export const INVOICE_REFERENCE_METHODS = [
  {
    value: ReferenceMethods.keep,
    labelKey: `constants.invoiceRepetition.referenceMethods.${ReferenceMethods.keep}`
  },
  {
    value: ReferenceMethods.roll,
    labelKey: `constants.invoiceRepetition.referenceMethods.${ReferenceMethods.roll}`
  }
]

export enum AttachmentAnalysisStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED'
}

export const ACC_TYPES_BANK = [AccountType.ASS_PAY]
export const ACC_TYPES_PURCHASE_PAYMENT = [AccountType.ASS_PAY, AccountType.LIA_DUE]
export const ACC_TYPES_SALES_PAYMENT = [AccountType.ASS_PAY, AccountType.ASS_DUE]
export const ACC_TYPES_PURCHASE = [AccountType.EXP, AccountType.EXP_NO, AccountType.EXP_50]
export const ACC_TYPES_SALES = [AccountType.REV, AccountType.REV_SAL, AccountType.REV_NO]
export const ACC_TYPES_ASSETS = [
  AccountType.ASS,
  AccountType.ASS_DEP,
  AccountType.ASS_VAT,
  AccountType.ASS_REC,
  AccountType.ASS_PAY,
  AccountType.ASS_DUE
]
export const ACC_TYPES_LIABILITIES = [
  AccountType.LIA,
  AccountType.LIA_EQU,
  AccountType.LIA_PRE,
  AccountType.LIA_DUE,
  AccountType.LIA_DEB,
  AccountType.LIA_ACC,
  AccountType.LIA_VAT
]
export const ACC_TYPES_SALES_AND_BALANCE_SHEET = [
  ...ACC_TYPES_SALES,
  ...ACC_TYPES_ASSETS,
  ...ACC_TYPES_LIABILITIES
]

export enum KraviaActivityStatus {
  WAITING_FOR_APPROVAL = 'WaitingForApproval',
  SCHEDULED = 'Scheduled',
  STARTED = 'Started',
  COMPLETED = 'Completed',
  CANCELED = 'Canceled'
}

export enum KraviaInvoiceStatus {
  ALL = 'All',
  READY_FOR_COLLECTION = 'ReadyForCollection',
  OPEN = 'Open',
  CLOSED = 'Closed',
  PAUSED = 'Paused',
  ACTION_REQUIRED = 'ActionRequired',
  WITHDRAWN = 'Withdrawn',
  INVOICE = 'Invoice',
  LEGAL_ACTION = 'LegalAction',
  CREDIT_NOTE = 'CreditNote',
  INVALID = 'Invalid',
  REMINDER = 'Reminder',
  MONITORING = 'Monitoring',
  ANONYMIZED = 'Anonymized'
}

export const KraviaTerminalInvoiceStatuses = [
  KraviaInvoiceStatus.CLOSED,
  KraviaInvoiceStatus.WITHDRAWN
]

export enum EnablebankingCashAccountType {
  CACC = 'CACC',
  CARD = 'CARD',
  CASH = 'CASH',
  LOAN = 'LOAN',
  OTHR = 'OTHR',
  SVGS = 'SVGS'
}

interface EnablebankingCashAccountTypeOption {
  labelKey: string
  value: EnablebankingCashAccountType
}

export const ENABLEBANKING_CASH_ACCOUNT_TYPE_OPTIONS: EnablebankingCashAccountTypeOption[] = [
  {
    labelKey: 'constants.enablebankingCashAccountType.CACC',
    value: EnablebankingCashAccountType.CACC
  },
  {
    labelKey: 'constants.enablebankingCashAccountType.CARD',
    value: EnablebankingCashAccountType.CARD
  },
  {
    labelKey: 'constants.enablebankingCashAccountType.CASH',
    value: EnablebankingCashAccountType.CASH
  },
  {
    labelKey: 'constants.enablebankingCashAccountType.LOAN',
    value: EnablebankingCashAccountType.LOAN
  },
  {
    labelKey: 'constants.enablebankingCashAccountType.OTHR',
    value: EnablebankingCashAccountType.OTHR
  },
  {
    labelKey: 'constants.enablebankingCashAccountType.SVGS',
    value: EnablebankingCashAccountType.SVGS
  }
]

export enum PurchaseInvoiceImportSource {
  APIX = 'APIX',
  EMAIL = 'EMAIL',
  UPLOAD = 'UPLOAD',
  EXPENSE_CLAIM = 'EXPENSE_CLAIM'
}

export enum PainStatus {
  PENDING = 'PDNG', // Pending further processing
  IN_PROCESSING = 'ACTC', // Technical validation passed
  ACCEPTED = 'ACCP', // Accepted technical, syntactical and profile; passed to back office
  ACCEPTED_WITH_CHANGE = 'ACWC', // Accepted with change
  ACCEPTED_PARTIALLY = 'PART', // Partially accepted and rejected
  REJECTED = 'RJCT' // Rejection
}

export enum TagColor {
  PURPLE = '#803FA5',
  BLUE = '#1558BC',
  GREEN = '#216E4E',
  TEAL = '#206A83',
  YELLOW = '#7F5F01',
  RED = '#AE2E24',
  GRAY = '#63666B',
  LIGHT_PURPLE = '#C97CF4',
  LIGHT_BLUE = '#669DF1',
  LIGHT_GREEN = '#4BCE97',
  LIGHT_TEAL = '#6CC3E0',
  LIGHT_YELLOW = '#FCA700',
  LIGHT_RED = '#F87168',
  LIGHT_GRAY = '#96999E'
}

export const TAG_COLORS = [
  TagColor.PURPLE,
  TagColor.BLUE,
  TagColor.GREEN,
  TagColor.TEAL,
  TagColor.YELLOW,
  TagColor.RED,
  TagColor.GRAY,
  TagColor.LIGHT_PURPLE,
  TagColor.LIGHT_BLUE,
  TagColor.LIGHT_GREEN,
  TagColor.LIGHT_TEAL,
  TagColor.LIGHT_YELLOW,
  TagColor.LIGHT_RED,
  TagColor.LIGHT_GRAY
]

export enum NocfoPermissionGroups {
  ADMIN = 'admin',
  READ_ONLY = 'read_only',
  BOOKKEEPING = 'bookkeeping',
  INVOICING = 'invoicing',
  EXPENSE_CLAIMS_ONLY = 'expense_claims_only',
  CUSTOM = 'custom'
}

// CAUTION: When adding/editing/removing new message types,
// ensure that the mobile app is updated to handle them.
export enum MobileEmbedMessages {
  BANK_ONBOARD_COMPLETE = 'bankonboarding-complete',
  HOLVI_ONBOARD_COMPLETE = 'holvionboarding-complete',
  ONBOARD_COMPLETE = 'onboarding-complete',
  ONBOARD_CANCEL = 'onboarding-cancel',
  BUSINESS_DELETED_COMPLETE = 'businessdelete-complete',
  RULE_FORM_CLOSE = 'ruleform-close',
  RULE_FORM_SUCCESS = 'ruleform-success'
}

export enum EmbedBankIntegrationRedirectPaths {
  ENABLE = 'enable_banking',
  HOLVI = 'holvi'
}

export enum MobileViewPath {
  ONBOARDING = 'onboarding',
  BANK_INTEGRATION_FLOW = 'bank-integration-flow',
  AI_CHAT = 'ai-chat',
  CUSTOMER_SERVICE = 'customer-service',
  SETTINGS_PEOPLE = 'settings/people',
  SETTINGS_BUSINESS = 'settings/business',
  SETTINGS_BANK_INTEGRATIONS = 'settings/bank-integrations',
  SETTINGS_RULES = 'settings/rules',
  SETTINGS_RULES_FORM = 'settings/rules/form'
}

export enum Conditions {
  EQUALS = 'eq',
  CONTAINS = 'cnt',
  LESS_THAN = 'lt',
  GREATER_THAN = 'gt',
  LESS_THAN_EQUAL = 'lte',
  GREATER_THAN_EQUAL = 'gte',
  CONTACT_IS_IN = 'contact_is_in'
}

export enum Fields {
  CONTACT = 'contact',
  MONEY = 'money',
  IBAN = 'iban',
  REFERENCE = 'reference',
  DESCRIPTION = 'description'
}

export enum Operands {
  AND = 'and',
  OR = 'or'
}

export enum RuleCreationSource {
  USER = 'user',
  SYSTEM = 'system',
  WORKFLOW = 'workflow'
}

export const PLUS_TRANSACTION_LIMIT = 80

export * from './debtCollectionDelay'
export * from './softReminderSteps'
